import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { domain } from './Domain.jsx';
import { AuthContext } from './AuthContext.jsx';
import useApiRequest from './useApiRequest.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import notify from './Nots.jsx';
import withAuthCheck from './WithAuthCheck.jsx';
import { saveToLocalStorage, getFromLocalStorage } from './EncryptDecrypt.jsx';
import { useLoginContext } from './LoginContext.jsx';
import { 
    Mail, Lock, Eye, EyeOff, User, CheckCircle, AlertCircle, 
    Loader2, ArrowRight, Shield, Zap, Star, Users, Calendar,
    Github, ArrowLeft
} from 'lucide-react';

const Login = () => {
    const { postRequest, loading } = useApiRequest();
    const { authToken, login, logout } = useContext(AuthContext);
    const { isLoggedIn, setIsLoggedIn, userInfo, setUserInfo } = useLoginContext();
    const [formData, setFormData] = useState({
        username: "",
        password: "",
    });
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const isLoginFormValid = formData.username.trim() !== '' && formData.password.trim() !== '';

    // Load saved credentials on component mount
    useEffect(() => {
        const savedCredentials = getFromLocalStorage("pub");
        if (savedCredentials) {
            setFormData({
                username: savedCredentials.username || "",
                password: savedCredentials.password || "",
            });
            setRememberMe(true);
        }
    }, []);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRememberMeChange = (e) => {
        setRememberMe(e.target.checked);
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            const req = await postRequest(`${domain}/accounts/login`, formData);
            if (req.status === 200) {
                notify('Login successful! Welcome back.', 'success');
                saveToLocalStorage("authInfo", req.body);
                localStorage.setItem("isLoggedIn", true);
                setIsLoggedIn(true);
                setUserInfo(req.body);
                
                if (rememberMe) {
                    saveToLocalStorage("pub", formData);
                } else {
                    localStorage.removeItem("pub");
                }
                
                setFormData({
                    username: "",
                    password: "",
                });
                navigate('/');
            } else if (req['details']?.['error']) {
                notify(req['details']['error'], "error");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Network error. Please try again.';
            notify(errorMsg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const socialLoginOptions = [];

    const features = [
        { icon: <Shield className="w-5 h-5" />, text: "Secure authentication" },
        { icon: <Zap className="w-5 h-5" />, text: "Lightning fast booking" },
        { icon: <Users className="w-5 h-5" />, text: "Connect with organizers" },
        { icon: <Calendar className="w-5 h-5" />, text: "Manage your events" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
            <div className="container mx-auto px-4 py-8 max-w-6xl">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
                    {/* Left Side - Login Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="flex justify-center"
                    >
                        <div className="w-full max-w-md">
                            {/* Back to Home */}
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="mb-6"
                            >
                                <Link 
                                    to="/" 
                                    className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 group"
                                >
                                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                    <span className="text-sm font-medium">Back to Home</span>
                                </Link>
                            </motion.div>

                            {/* Login Form */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
                            >
                                {/* Header */}
                                <div className="text-center mb-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                        <User className="w-8 h-8 text-white" />
                                    </div>
                                    <h1 className="text-lg font-bold text-gray-900 mb-2">Welcome Back</h1>
                                    <p className="text-sm text-gray-600">Sign in to your EventPlots account</p>
                                </div>

                                {/* Login Form */}
                                <form onSubmit={handleLogin} className="space-y-6">
                                    {/* Username Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Username
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                name="username"
                                                value={formData.username}
                                                onChange={handleInputChange}
                                                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                                                    errors.username ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                                placeholder="Enter your username"
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {errors.username && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.username}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Password Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                className={`w-full pl-9 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                                                    errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                                placeholder="Enter your password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <AnimatePresence>
                                            {errors.password && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -10 }}
                                                    className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                    {errors.password}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* Remember Me & Forgot Password */}
                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={handleRememberMeChange}
                                                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                                            />
                                            <span className="text-sm text-gray-700">Remember me</span>
                                        </label>
                                        <Link
                                            to="/forgot-password"
                                            className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors duration-200"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={!isLoginFormValid || isSubmitting}
                                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 px-4 rounded-lg font-medium transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                Signing in...
                                            </>
                                        ) : (
                                            <>
                                                Sign In
                                                <ArrowRight className="w-5 h-5" />
                                            </>
                                        )}
                                    </button>
                                </form>

                                {/* Divider */}
                                <div className="my-6 flex items-center">
                                    <div className="flex-1 border-t border-gray-200"></div>
                                    <span className="px-4 text-sm text-gray-500">or</span>
                                    <div className="flex-1 border-t border-gray-200"></div>
                                </div>

                                {/* Social Login */}
                                {socialLoginOptions.length > 0 && (
                                    <div className="space-y-3">
                                        {socialLoginOptions.map((option, index) => (
                                            <button
                                                key={index}
                                                className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                                            >
                                                {option.icon}
                                                {option.text}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Sign Up Link */}
                                <div className="text-center mt-6">
                                    <p className="text-sm text-gray-600">
                                        Don't have an account?{' '}
                                        <Link
                                            to="/register"
                                            className="text-orange-600 hover:text-orange-700 font-medium transition-colors duration-200"
                                        >
                                            Sign up here
                                        </Link>
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Right Side - Features */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="hidden lg:block"
                    >
                        <div className="max-w-lg">
                            <div className="mb-8">
                                <h2 className="text-lg font-bold text-gray-900 mb-4">
                                    Welcome to EventPlots
                                </h2>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    Your gateway to discovering and creating amazing events. Connect with organizers, 
                                    book tickets seamlessly, and be part of unforgettable experiences.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                                        className="flex items-center gap-3"
                                    >
                                        <div className="w-10 h-10 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg flex items-center justify-center">
                                            <div className="text-orange-600">
                                                {feature.icon}
                                            </div>
                                        </div>
                                        <span className="text-sm text-gray-700 font-medium">{feature.text}</span>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Trust Indicators */}
                            <div className="mt-8 p-6 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20">
                                <div className="flex items-center gap-2 mb-3">
                                    <Shield className="w-5 h-5 text-orange-600" />
                                    <span className="text-sm font-semibold text-gray-900">Secure & Trusted</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Your data is protected with enterprise-grade security. Join thousands of users 
                                    who trust EventPlots for their event needs.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

// Wrap the Login component with the HOC
export default withAuthCheck(Login);